import { ReactNode } from 'react';

import * as St from '../Client/styles';
import { formatDate, formatMoney } from './format';

type NegotiationType = 'avista' | 'parcelado';

interface NegotiationOfferProps {
  type: NegotiationType;
  locked?: boolean;
  onTypeChange?: (type: NegotiationType) => void;
  entryValue?: number;
  discountValue: string;
  onDiscountChange?: (value: string) => void;
  onDiscountBlur?: () => void;
  installments?: number | '';
  installmentOptions?: number[];
  onInstallmentsChange?: (value: number) => void;
  installmentValue?: number;
  invoiceValue?: number;
  dueDate?: string;
  appliedDiscount?: number;
  debtTotal?: number;
  actions?: ReactNode;
}

const NegotiationOffer = ({
  type,
  locked,
  onTypeChange,
  entryValue,
  discountValue,
  onDiscountChange,
  onDiscountBlur,
  installments,
  installmentOptions = [],
  onInstallmentsChange,
  installmentValue,
  invoiceValue,
  dueDate,
  appliedDiscount,
  debtTotal,
  actions,
}: NegotiationOfferProps): JSX.Element => {
  const installmentCount = Number(installments) || 0;
  const installmentLabel = installmentCount
    ? `${installmentCount} ${installmentCount === 1 ? 'parcela' : 'parcelas'}`
    : '-';

  return (
    <>
      <St.SummaryTabs>
        <St.SummaryTab
          as={onTypeChange ? 'button' : 'div'}
          type={onTypeChange ? 'button' : undefined}
          $active={type === 'avista'}
          disabled={locked}
          onClick={onTypeChange ? () => onTypeChange('avista') : undefined}
        >
          À vista
        </St.SummaryTab>
        <St.SummaryTab
          as={onTypeChange ? 'button' : 'div'}
          type={onTypeChange ? 'button' : undefined}
          $active={type === 'parcelado'}
          disabled={locked}
          onClick={onTypeChange ? () => onTypeChange('parcelado') : undefined}
        >
          Parcelado
        </St.SummaryTab>
      </St.SummaryTabs>
      <St.EntryRow>
        <St.EntryColumn>
          <St.EntryLabel>Valor da entrada</St.EntryLabel>
          <St.Amount>{formatMoney(entryValue)}</St.Amount>
        </St.EntryColumn>
        <St.EntryColumn $end>
          <St.EntryLabel>Entrada (%)</St.EntryLabel>
          <St.PercentGroup>
            <St.PercentField
              value={discountValue}
              inputMode="numeric"
              readOnly={!onDiscountChange}
              disabled={locked}
              onChange={
                onDiscountChange
                  ? (event) => onDiscountChange(event.target.value)
                  : undefined
              }
              onBlur={onDiscountBlur}
            />
            <St.PercentSuffix>%</St.PercentSuffix>
          </St.PercentGroup>
        </St.EntryColumn>
      </St.EntryRow>
      {type === 'avista' ? (
        <St.InfoNotice>
          <St.InfoBadge>i</St.InfoBadge>
          <span>
            A nova fatura será gerada com o valor da entrada para negociação à
            vista.
          </span>
        </St.InfoNotice>
      ) : (
        <>
          <St.InfoNotice>
            <St.InfoBadge>i</St.InfoBadge>
            <span>
              Após o pagamento da entrada, as parcelas serão lançadas
              mensalmente.
            </span>
          </St.InfoNotice>
          <St.InstallmentRow>
            <St.EntryColumn>
              <St.EntryLabel>Valor da entrada</St.EntryLabel>
              <St.Amount>{formatMoney(entryValue)}</St.Amount>
            </St.EntryColumn>
            <St.EntryColumn>
              <St.EntryLabel>Número de parcelas</St.EntryLabel>
              {onInstallmentsChange ? (
                <St.InstallmentField
                  as="select"
                  value={installments}
                  disabled={locked || installmentOptions.length === 0}
                  onChange={(event) =>
                    onInstallmentsChange(Number(event.target.value))
                  }
                >
                  {installmentOptions.length === 0 ? (
                    <option value="">-</option>
                  ) : (
                    installmentOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                        {' '}
                        {option === 1 ? 'parcela' : 'parcelas'}
                      </option>
                    ))
                  )}
                </St.InstallmentField>
              ) : (
                <St.Amount>{installmentLabel}</St.Amount>
              )}
            </St.EntryColumn>
            <St.EntryColumn>
              <St.EntryLabel>Valor das parcelas</St.EntryLabel>
              <St.Amount>{formatMoney(installmentValue)}</St.Amount>
              <St.MutedText>Taxa de juros: 5,9% ao mês</St.MutedText>
            </St.EntryColumn>
          </St.InstallmentRow>
        </>
      )}
      <St.NewInvoice>
        <St.PanelTitle>Nova fatura (Após acordo)</St.PanelTitle>
        {dueDate !== undefined && (
          <St.EntryColumn>
            <St.PlainText>Vencimento</St.PlainText>
            <St.Amount>{formatDate(dueDate)}</St.Amount>
          </St.EntryColumn>
        )}
        <St.NewInvoiceValue>
          <St.PlainText>Valor da nova fatura</St.PlainText>
          <St.DiscountAmount>{formatMoney(invoiceValue)}</St.DiscountAmount>
        </St.NewInvoiceValue>
      </St.NewInvoice>
      <div>
        <St.PanelTitle>Resumo da negociação</St.PanelTitle>
        <St.ConfigBlock>
          <St.SummaryLine>
            <St.EntryLabel>Desconto aplicado</St.EntryLabel>
            <St.DiscountAmount>
              -
              {' '}
              {formatMoney(appliedDiscount)}
            </St.DiscountAmount>
          </St.SummaryLine>
          <St.SummaryLine>
            <St.EntryLabel>Total da dívida</St.EntryLabel>
            <St.PlainText>{formatMoney(debtTotal)}</St.PlainText>
          </St.SummaryLine>
          <St.WarningNotice>
            <St.WarningBadge>
              <span>i</span>
            </St.WarningBadge>
            <span>
              O acordo será representado por uma nova fatura que será gerada
              com o vencimento em até 15 dias
            </span>
          </St.WarningNotice>
          {actions}
        </St.ConfigBlock>
      </div>
    </>
  );
};

export default NegotiationOffer;
